import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { Config } from '../../../App';

interface ThemeEditorProps {
  config: Config | null;
  onThemeChange: (key: string, value: any) => void;
  onOpenFileSelector: (field: string) => void;
}

const ColorInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => (
  <TextField
    label={label}
    value={value || '#000000'}
    onChange={(e) => onChange(e.target.value)}
    fullWidth
    variant="outlined"
    size="small"
    sx={{ mb: 2 }}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <Box
            component="input"
            type="color"
            value={value || '#000000'}
            onChange={(e: any) => onChange(e.target.value)}
            sx={{
              width: '24px',
              height: '24px',
              border: 'none',
              padding: 0,
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          />
        </InputAdornment>
      ),
      sx: { fontFamily: 'monospace' },
    }}
  />
);

const ThemeEditor: React.FC<ThemeEditorProps> = ({
  config,
  onThemeChange,
  onOpenFileSelector,
}) => {
  if (!config) return null;

  return (
    <Grid container spacing={3}>
      {/* BUTTONS AND LINKS */}
      <Grid size={{ xs: 12, md: 3 }}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="subtitle2" sx={{ mb: 3 }}>
            BUTTONS AND LINKS
          </Typography>

          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}
          >
            PRIMARY
          </Typography>
          <ColorInput
            label="Primary main"
            value={config.theme.palette?.primary?.main}
            onChange={(val) => onThemeChange('palette.primary.main', val)}
          />
          <ColorInput
            label="Hover"
            value={config.theme.palette?.primary?.dark}
            onChange={(val) => onThemeChange('palette.primary.dark', val)}
          />
          <ColorInput
            label="Text"
            value={config.theme.palette?.primary?.contrastText}
            onChange={(val) =>
              onThemeChange('palette.primary.contrastText', val)
            }
          />

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              mt: 2,
              fontWeight: 'bold',
            }}
          >
            SECONDARY
          </Typography>
          <ColorInput
            label="Secondary main"
            value={config.theme.palette?.secondary?.main}
            onChange={(val) => onThemeChange('palette.secondary.main', val)}
          />
          <ColorInput
            label="Hover"
            value={config.theme.palette?.secondary?.dark}
            onChange={(val) => onThemeChange('palette.secondary.dark', val)}
          />
          <ColorInput
            label="Text"
            value={config.theme.palette?.secondary?.contrastText}
            onChange={(val) =>
              onThemeChange('palette.secondary.contrastText', val)
            }
          />

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              mt: 2,
              fontWeight: 'bold',
            }}
          >
            TEXT LINK COLOR
          </Typography>
          <ColorInput
            label="Text link"
            value={
              config.theme.components?.MuiButton?.styleOverrides?.text?.color
            }
            onChange={(val) =>
              onThemeChange(
                'components.MuiButton.styleOverrides.text.color',
                val
              )
            }
          />
        </Paper>
      </Grid>

      {/* PAGE */}
      <Grid size={{ xs: 12, md: 3 }}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="subtitle2" sx={{ mb: 3 }}>
            PAGE
          </Typography>

          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}
          >
            TEXT
          </Typography>
          <ColorInput
            label="Text"
            value={config.theme.palette?.text?.primary}
            onChange={(val) => onThemeChange('palette.text.primary', val)}
          />

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              mt: 2,
              fontWeight: 'bold',
            }}
          >
            BACKGROUND
          </Typography>
          <ColorInput
            label="Background"
            value={config.theme.palette?.background?.default}
            onChange={(val) => onThemeChange('palette.background.default', val)}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!!config.theme.backgroundImage}
                onChange={(e) => {
                  if (!e.target.checked) {
                    onThemeChange('backgroundImage', '');
                  }
                }}
              />
            }
            label={
              <Typography variant="body2">Use background image</Typography>
            }
            sx={{ mt: 1 }}
          />

          <Box
            sx={{
              border: '1px dashed #ccc',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              mt: 1,
              cursor: 'pointer',
              bgcolor: '#fafafa',
            }}
            onClick={() => onOpenFileSelector('backgroundImage')}
          >
            <UploadFile sx={{ color: '#1976d2', mb: 1 }} />
            <Typography
              variant="body2"
              color="primary"
              sx={{ fontWeight: 'bold' }}
            >
              Select image
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              SVG, PNG, JPG or GIF (max. 3MB)
            </Typography>
            {config.theme.backgroundImage && (
              <Typography
                variant="caption"
                display="block"
                noWrap
                sx={{ mt: 1 }}
              >
                Current: {config.theme.backgroundImage.split('/').pop()}
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>

      {/* NAVIGATION */}
      <Grid size={{ xs: 12, md: 3 }}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="subtitle2" sx={{ mb: 3 }}>
            NAVIGATION
          </Typography>

          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}
          >
            TEXT
          </Typography>
          <ColorInput
            label="Text"
            value={
              config.theme.components?.MuiAppBar?.styleOverrides?.root?.color
            }
            onChange={(val) =>
              onThemeChange(
                'components.MuiAppBar.styleOverrides.root.color',
                val
              )
            }
          />

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              mt: 2,
              fontWeight: 'bold',
            }}
          >
            BACKGROUND
          </Typography>
          <ColorInput
            label="Background"
            value={
              config.theme.components?.MuiAppBar?.styleOverrides?.root
                ?.backgroundColor
            }
            onChange={(val) =>
              onThemeChange(
                'components.MuiAppBar.styleOverrides.root.backgroundColor',
                val
              )
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.navigationConfig?.showLogo}
                onChange={(e) => {
                  // Need to handle navigationConfig update separately or add it to handleThemeChange logic if it supports root config
                  // For now assuming handleThemeChange only handles theme.
                  // We need a handleConfigChange for root properties.
                }}
              />
            }
            label={<Typography variant="body2">Show logo on left</Typography>}
            sx={{ mt: 1 }}
          />

          <Box
            sx={{
              border: '1px dashed #ccc',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              mt: 1,
              cursor: 'pointer',
              bgcolor: '#fafafa',
            }}
            onClick={() => onOpenFileSelector('navigationConfig.logo.url')}
          >
            <UploadFile sx={{ color: '#1976d2', mb: 1 }} />
            <Typography
              variant="body2"
              color="primary"
              sx={{ fontWeight: 'bold' }}
            >
              Select image
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              SVG, PNG, JPG or GIF (max. 3MB)
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox checked={config.navigationConfig?.lastItemIsButton} />
            }
            label={
              <Typography variant="body2">
                Show last item as a button
              </Typography>
            }
            sx={{ mt: 2 }}
          />

          <Typography variant="caption" sx={{ display: 'block', mb: 1, mt: 1 }}>
            Button color
          </Typography>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 1,
            }}
          >
            <Typography variant="body2">default (primary)</Typography>
          </Box>
        </Paper>
      </Grid>

      {/* FOOTER */}
      <Grid size={{ xs: 12, md: 3 }}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="subtitle2" sx={{ mb: 3 }}>
            FOOTER
          </Typography>

          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}
          >
            TEXT
          </Typography>
          <ColorInput
            label="Text"
            value={config.theme.components?.Footer?.styleOverrides?.root?.color}
            onChange={(val) =>
              onThemeChange('components.Footer.styleOverrides.root.color', val)
            }
          />

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              mt: 2,
              fontWeight: 'bold',
            }}
          >
            BACKGROUND
          </Typography>
          <ColorInput
            label="Background"
            value={
              config.theme.components?.Footer?.styleOverrides?.root
                ?.backgroundColor
            }
            onChange={(val) =>
              onThemeChange(
                'components.Footer.styleOverrides.root.backgroundColor',
                val
              )
            }
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ThemeEditor;
